import { Component, OnInit, signal, computed, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CopilotApiService } from '../../services/copilot-api-service';
import { BusinessPartner } from '../../model/business_partner';
import { BusinessPartnerCard } from './card/business-partner-card';
import { AddBusinessPartnerDialog } from './dialog/add-business-partner-dialog';
import { AuthService, User } from '@auth0/auth0-angular';
import { ToastService } from '../../services/toast.service';
import { BusinessPartnersService } from '../../common/services/business-partners.service';

@Component({
  selector: 'app-business-partners',
  standalone: true,
  imports: [CommonModule, TranslateModule, BusinessPartnerCard, AddBusinessPartnerDialog],
  templateUrl: './business-partners.html',
  styleUrl: './business-partners.css'
})
export class BusinessPartners implements OnInit {
  protected readonly partners = signal<BusinessPartner[] | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);
  protected readonly showAddDialog = signal<boolean>(false);
  protected readonly submitting = signal<boolean>(false);
  protected readonly showDisabled = signal<boolean>(false);
  protected readonly selectedLevel = signal<number | 'ALL'>('ALL');
  protected openLevel: boolean = false;
  protected readonly levels = computed<number[]>(() => {
    const list = this.partners() || [];
    const unique = Array.from(new Set(list.map(p => p.Level)));
    return unique.sort((a, b) => a - b);
  });
  protected readonly filteredPartners = computed(() => {
    const selected = this.selectedLevel();
    let list = this.partners() || [];
    if (!this.showDisabled()) list = list.filter(p => p.Enabled);
    if (selected !== 'ALL') list = list.filter(p => p.Level === selected);
    return list;
  });

  private currentUser: User | null | undefined = null;

  constructor(
    private api: CopilotApiService,
    private auth: AuthService,
    private translate: TranslateService,
    private toast: ToastService,
    private partnersService: BusinessPartnersService
  ) {}

  ngOnInit(): void {
    this.partnersService.initialize();
    this.partnersService.partners$.subscribe((data) => {
      this.partners.set((data || []).slice());
    });
    this.partnersService.partnersLoading$.subscribe((loading) => {
      this.loading.set(loading);
    });
    this.partnersService.partnersError$.subscribe((errorCode) => {
      this.error.set(errorCode ? this.translate.instant('business_partners.loading_error') : null);
    });
    this.auth.user$.subscribe(u => this.currentUser = u);
  }

  protected toggleLevelOpen(event?: Event): void {
    event?.stopPropagation();
    this.openLevel = !this.openLevel;
  }

  protected onSelectLevel(val: number | 'ALL', event?: Event): void {
    event?.stopPropagation();
    this.selectedLevel.set(val);
    this.openLevel = false;
  }

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.openLevel = false;
  }

  protected openAddDialog(): void { this.showAddDialog.set(true); }
  protected closeAddDialog(): void { this.showAddDialog.set(false); }
  protected onPartnerUpdated(): void { this.partnersService.refresh(); }
  protected submitNewPartner(form: { Name: string; LastName: string; Email: string; Phone: string; CompanyName: string; }): void {
    const userEmail = this.currentUser?.email || null;
    const payload: BusinessPartner = {
      Id: null,
      ParentId: null,
      Name: form.Name,
      LastName: form.LastName,
      Phone: form.Phone,
      Email: form.Email,
      CompanyName: form.CompanyName,
      Level: 0,
      Enabled: true,
      CreatedBy: userEmail,
      CreatedDate: null
    };
    this.submitting.set(true);
    this.api.addBusinessPartner(payload).subscribe({
      next: () => {
        this.partnersService.refresh();
        this.toast.success(this.translate.instant('business_partners.add_success_toast'), 3000);
        this.submitting.set(false);
        this.closeAddDialog();
      },
      error: (err) => {
        this.toast.error(this.translate.instant('business_partners.add_error_toast'), 3000);
        this.submitting.set(false);
        this.closeAddDialog();
        console.error(err);
      }
    })
  }
}
